// components/OwnerContext.tsx
// Owner-only dashboard: analytics, expenses, manage doctors, clinic revenue, etc.
// Legacy: doctors without is_owner (undefined) treated as owner; explicit false = not owner.
import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { isViewerOnlySession, setViewerOnlySession } from '../utils/viewerOnly';

interface OwnerContextType {
  /** True if current doctor can view revenue UI/reports. Owners always can. */
  canSeeRevenueAnalytics: boolean;
  /** True only for owners/legacy owners. Used for manage-doctors/export tools. */
  canManageOwnerFeatures: boolean;
  /** Owners by default; optionally granted to a linked doctor. */
  canSeeDoctorAttendanceReport: boolean;
  /** Owners by default; optionally granted to a linked doctor. */
  canAccessInsurance: boolean;
  /** True when this doctor's account may read data but may not mutate it. */
  isViewerOnly: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

const OwnerContext = createContext<OwnerContextType | undefined>(undefined);

const getDoctorId = () => localStorage.getItem('doctorId');
const getClinicId = () => localStorage.getItem('clinicId');

export const OwnerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [canSeeRevenueAnalytics, setCanSeeRevenueAnalytics] = useState<boolean>(false);
  const [canManageOwnerFeatures, setCanManageOwnerFeatures] = useState<boolean>(false);
  const [canSeeDoctorAttendanceReport, setCanSeeDoctorAttendanceReport] = useState<boolean>(false);
  const [canAccessInsurance, setCanAccessInsurance] = useState<boolean>(false);
  const [isViewerOnly, setIsViewerOnly] = useState<boolean>(() => isViewerOnlySession());
  const [loading, setLoading] = useState<boolean>(true);

  const fetchOwnerFlag = useCallback(async () => {
    const doctorId = getDoctorId();
    if (!doctorId) {
      setCanSeeRevenueAnalytics(false);
      setCanManageOwnerFeatures(false);
      setCanSeeDoctorAttendanceReport(false);
      setCanAccessInsurance(false);
      setIsViewerOnly(false);
      setViewerOnlySession(false);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const doctorRef = doc(db, 'doctors', doctorId);
      const clinicId = getClinicId();
      const [snap, clinicSnap] = await Promise.all([
        getDoc(doctorRef),
        clinicId && clinicId !== '__all_clinics__'
          ? getDoc(doc(db, 'clinics', clinicId))
          : Promise.resolve(null),
      ]);
      const data = snap.exists() ? snap.data() : null;
      const isOwner = data?.is_owner;
      const explicitOwnerId = clinicSnap?.exists()
        ? String(clinicSnap.data()?.owner_doctor_id || '').trim()
        : '';
      // Explicit clinic ownership is identity-bound so an assistant linked to
      // the owner doctor cannot inherit owner permissions. Clinics without the
      // new field retain the exact legacy is_owner behaviour.
      const ownerAccess = explicitOwnerId
        ? explicitOwnerId === doctorId
          && Boolean(auth.currentUser?.uid)
          && String(data?.auth_uid || '').trim() === auth.currentUser?.uid
        : isOwner === true || isOwner === undefined;
      const revenuePrivilege = data?.privileges?.showRevenue === true;
      const attendancePrivilege = data?.privileges?.viewDoctorAttendance === true;
      const insurancePrivilege = data?.privileges?.manageInsurance === true;
      const viewerOnlyPrivilege = data?.privileges?.viewerOnly === true;
      setCanManageOwnerFeatures(ownerAccess);
      setCanSeeRevenueAnalytics(ownerAccess || revenuePrivilege);
      setCanSeeDoctorAttendanceReport(ownerAccess || attendancePrivilege);
      setCanAccessInsurance(ownerAccess || insurancePrivilege);
      setIsViewerOnly(viewerOnlyPrivilege);
      setViewerOnlySession(viewerOnlyPrivilege);
    } catch (err) {
      console.error('Error fetching doctor owner flag:', err);
      setCanSeeRevenueAnalytics(false);
      setCanManageOwnerFeatures(false);
      setCanSeeDoctorAttendanceReport(false);
      setCanAccessInsurance(false);
      // A transient Firestore failure must not turn a known viewer session
      // into an editable one. A successful refresh will clear the flag when
      // the owner revokes the privilege.
      const retainedViewerOnly = isViewerOnlySession();
      setIsViewerOnly(retainedViewerOnly);
      setViewerOnlySession(retainedViewerOnly);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOwnerFlag();
    const onDoctorIdChange = () => fetchOwnerFlag();
    window.addEventListener('doctorIdChanged', onDoctorIdChange);
    window.addEventListener('doctorPrivilegesChanged', onDoctorIdChange);
    window.addEventListener('clinicIdChanged', onDoctorIdChange);
    return () => {
      window.removeEventListener('doctorIdChanged', onDoctorIdChange);
      window.removeEventListener('doctorPrivilegesChanged', onDoctorIdChange);
      window.removeEventListener('clinicIdChanged', onDoctorIdChange);
    };
  }, [fetchOwnerFlag]);

  const value: OwnerContextType = {
    canSeeRevenueAnalytics,
    canManageOwnerFeatures,
    canSeeDoctorAttendanceReport,
    canAccessInsurance,
    isViewerOnly,
    loading,
    refresh: fetchOwnerFlag,
  };

  return (
    <OwnerContext.Provider value={value}>
      {children}
    </OwnerContext.Provider>
  );
};

export const useOwnerContext = (): OwnerContextType => {
  const context = useContext(OwnerContext);
  if (context === undefined) {
    throw new Error('useOwnerContext must be used within an OwnerProvider');
  }
  return context;
};
